/**
 * Settings Page
 *
 * User settings and integrations configuration.
 * Includes admin-only tabs for cost tracking, jobs, and logs.
 */

import { useState, useEffect } from 'react';
import { LinkedInSetup } from '../components/settings/LinkedInSetup';
import { SystemAdminOnly } from '../components/common/RoleGate';
import { useAuth } from '../contexts/AuthContext';
import {
  CostSummaryCard,
  CostByProviderChart,
  CostProjectionCard,
  AdminJobsSection,
  AdminLogsSection,
  AdminUserStatsSection,
} from '../components/admin';
import {
  useAdminCostSummary,
  useAdminCostsByProvider,
  useAdminCostProjection,
} from '../hooks/useAdminCosts';

type AdminTab = 'cost' | 'user-stats' | 'jobs' | 'logs' | 'kb-management';

interface BraveStatus {
  maintenance_mode: boolean;
  cache_enabled: boolean;
  cache_hits: number;
  cache_misses: number;
  cache_size: number;
  cache_hit_rate: number;
}

function AdminKBManagementSection() {
  const { accessToken } = useAuth();
  const [dedupeStatus, setDedupeStatus] = useState<'idle' | 'running' | 'success' | 'error'>(
    'idle'
  );
  const [rebuildStatus, setRebuildStatus] = useState<'idle' | 'running' | 'success' | 'error'>(
    'idle'
  );
  const [clearJobsStatus, setClearJobsStatus] = useState<'idle' | 'running' | 'success' | 'error'>(
    'idle'
  );
  const [resetCostsStatus, setResetCostsStatus] = useState<
    'idle' | 'running' | 'success' | 'error'
  >('idle');
  const [dedupeResult, setDedupeResult] = useState<{
    total_removed: number;
    companies: string[];
  } | null>(null);
  const [rebuildResult, setRebuildResult] = useState<{ triggered_count: number } | null>(null);
  const [clearJobsResult, setClearJobsResult] = useState<{ cleared_count: number } | null>(null);
  const [resetCostsResult, setResetCostsResult] = useState<{
    api_usage_deleted: number;
    build_metrics_deleted: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Brave Search maintenance mode state
  const [braveStatus, setBraveStatus] = useState<BraveStatus | null>(null);
  const [braveLoading, setBraveLoading] = useState(true);
  const [braveToggling, setBraveToggling] = useState(false);

  // Fetch Brave status when accessToken becomes available
  useEffect(() => {
    if (!accessToken) {
      setBraveLoading(false); // Not loading if no token yet
      return;
    }

    setBraveLoading(true); // Reset loading state when token changes
    const fetchBraveStatus = async () => {
      try {
        const response = await fetch('/api/admin/brave/status', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setBraveStatus(data);
        }
      } catch (err) {
        console.error('Failed to fetch Brave status:', err);
      } finally {
        setBraveLoading(false);
      }
    };
    fetchBraveStatus();
  }, [accessToken]);

  const handleToggleBraveMaintenanceMode = async () => {
    if (!braveStatus) return;
    setBraveToggling(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/brave/maintenance-mode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        body: JSON.stringify({ enabled: !braveStatus.maintenance_mode }),
      });
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      const data = await response.json();
      setBraveStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle maintenance mode');
    } finally {
      setBraveToggling(false);
    }
  };

  const handleClearBraveCache = async () => {
    setError(null);
    try {
      const response = await fetch('/api/admin/brave/clear-cache', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      // Refresh status to update cache stats
      const statusResponse = await fetch('/api/admin/brave/status', {
        headers: {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      });
      if (statusResponse.ok) {
        setBraveStatus(await statusResponse.json());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear cache');
    }
  };

  const handleDeduplicateAll = async () => {
    setDedupeStatus('running');
    setError(null);
    try {
      const response = await fetch('/api/admin/executives/deduplicate-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      const data = await response.json();
      setDedupeResult(data);
      setDedupeStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setDedupeStatus('error');
    }
  };

  const handleRebuildAllKBs = async () => {
    setRebuildStatus('running');
    setError(null);
    try {
      const response = await fetch('/api/admin/kb/rebuild-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      const data = await response.json();
      setRebuildResult(data);
      setRebuildStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setRebuildStatus('error');
    }
  };

  const handleClearAllJobs = async () => {
    setClearJobsStatus('running');
    setError(null);
    try {
      const response = await fetch('/api/admin/jobs/clear-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      const data = await response.json();
      setClearJobsResult(data);
      setClearJobsStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setClearJobsStatus('error');
    }
  };

  const handleResetCosts = async () => {
    setResetCostsStatus('running');
    setError(null);
    try {
      const response = await fetch('/api/admin/reset-costs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      });
      if (!response.ok) {
        throw new Error(`Failed: ${response.status}`);
      }
      const data = await response.json();
      setResetCostsResult(data);
      setResetCostsStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setResetCostsStatus('error');
    }
  };

  return (
    <section className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">KB Management</h2>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Brave Search Controls */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-md font-medium text-gray-900">Brave Search API</h3>
            <p className="text-sm text-gray-600">Control Brave Search API usage to manage costs.</p>
          </div>
          {!braveLoading && braveStatus && (
            <div className="flex items-center gap-4">
              <div className="text-right text-sm text-gray-500">
                <div>
                  Cache: {braveStatus.cache_size} items ({braveStatus.cache_hit_rate}% hit rate)
                </div>
                <div>
                  Hits: {braveStatus.cache_hits} / Misses: {braveStatus.cache_misses}
                </div>
              </div>
              <button
                onClick={handleClearBraveCache}
                className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear Cache
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <div className="font-medium text-gray-900">Maintenance Mode</div>
            <div className="text-sm text-gray-600">
              {braveStatus?.maintenance_mode
                ? 'API calls disabled - searches return empty results'
                : 'API calls enabled - searches hit Brave API'}
            </div>
          </div>
          <button
            onClick={handleToggleBraveMaintenanceMode}
            disabled={braveLoading || braveToggling}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              braveLoading || braveToggling
                ? 'bg-gray-300 cursor-not-allowed'
                : braveStatus?.maintenance_mode
                  ? 'bg-orange-500'
                  : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                braveStatus?.maintenance_mode ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {braveStatus?.maintenance_mode && (
          <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
            <p className="text-sm text-orange-800">
              Brave Search is in maintenance mode. KB builds will skip web searches. Toggle off to
              resume normal operation.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deduplicate Executives */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-md font-medium text-gray-900 mb-2">Deduplicate Executives</h3>
          <p className="text-sm text-gray-600 mb-4">
            Remove duplicate executive records across all companies using fuzzy name matching.
          </p>
          <button
            onClick={handleDeduplicateAll}
            disabled={dedupeStatus === 'running'}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              dedupeStatus === 'running'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {dedupeStatus === 'running' ? 'Running...' : 'Deduplicate All'}
          </button>
          {dedupeResult && dedupeStatus === 'success' && (
            <div className="mt-4 p-3 bg-green-50 rounded-md">
              <p className="text-sm text-green-700">
                Removed {dedupeResult.total_removed} duplicates from {dedupeResult.companies.length}{' '}
                companies
              </p>
            </div>
          )}
        </div>

        {/* Rebuild All KBs */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-md font-medium text-gray-900 mb-2">Rebuild All KBs</h3>
          <p className="text-sm text-gray-600 mb-4">
            Trigger a full KB rebuild for all companies. This may take several minutes.
          </p>
          <button
            onClick={handleRebuildAllKBs}
            disabled={rebuildStatus === 'running'}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              rebuildStatus === 'running'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-orange-600 text-white hover:bg-orange-700'
            }`}
          >
            {rebuildStatus === 'running' ? 'Triggering...' : 'Rebuild All KBs'}
          </button>
          {rebuildResult && rebuildStatus === 'success' && (
            <div className="mt-4 p-3 bg-green-50 rounded-md">
              <p className="text-sm text-green-700">
                Triggered KB builds for {rebuildResult.triggered_count} companies
              </p>
            </div>
          )}
        </div>

        {/* Clear All Jobs */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-md font-medium text-gray-900 mb-2">Clear All Jobs</h3>
          <p className="text-sm text-gray-600 mb-4">
            Cancel all pending and running jobs. Use this to reset the job queue when jobs are
            stuck.
          </p>
          <button
            onClick={handleClearAllJobs}
            disabled={clearJobsStatus === 'running'}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              clearJobsStatus === 'running'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {clearJobsStatus === 'running' ? 'Clearing...' : 'Clear All Jobs'}
          </button>
          {clearJobsResult && clearJobsStatus === 'success' && (
            <div className="mt-4 p-3 bg-green-50 rounded-md">
              <p className="text-sm text-green-700">
                Cleared {clearJobsResult.cleared_count} jobs from queue
              </p>
            </div>
          )}
        </div>

        {/* Reset Cost Tracking */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-md font-medium text-gray-900 mb-2">Reset Cost Tracking</h3>
          <p className="text-sm text-gray-600 mb-4">
            Clear all API usage and build metrics data. Use this for a fresh start on cost tracking.
          </p>
          <button
            onClick={handleResetCosts}
            disabled={resetCostsStatus === 'running'}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              resetCostsStatus === 'running'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            {resetCostsStatus === 'running' ? 'Resetting...' : 'Reset Costs'}
          </button>
          {resetCostsResult && resetCostsStatus === 'success' && (
            <div className="mt-4 p-3 bg-green-50 rounded-md">
              <p className="text-sm text-green-700">
                Deleted {resetCostsResult.api_usage_deleted} API records and{' '}
                {resetCostsResult.build_metrics_deleted} build metrics
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function AdminCostSection() {
  const [days, setDays] = useState(30);

  const summaryQuery = useAdminCostSummary(days);
  const providerQuery = useAdminCostsByProvider(days);
  const projectionQuery = useAdminCostProjection(3);

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Cost & Usage Metrics</h2>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <CostSummaryCard
        data={summaryQuery.data}
        isLoading={summaryQuery.isLoading}
        error={summaryQuery.error}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <CostByProviderChart
            data={providerQuery.data}
            isLoading={providerQuery.isLoading}
            error={providerQuery.error}
          />
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <CostProjectionCard
            data={projectionQuery.data}
            isLoading={projectionQuery.isLoading}
            error={projectionQuery.error}
          />
        </div>
      </div>
    </section>
  );
}

interface AdminTabButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function AdminTabButton({ label, isActive, onClick }: AdminTabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
        isActive
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  );
}

function AdminSection() {
  const [activeTab, setActiveTab] = useState<AdminTab>('user-stats');

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <AdminTabButton
            label="User Statistics"
            isActive={activeTab === 'user-stats'}
            onClick={() => setActiveTab('user-stats')}
          />
          <AdminTabButton
            label="Cost & Usage"
            isActive={activeTab === 'cost'}
            onClick={() => setActiveTab('cost')}
          />
          <AdminTabButton
            label="Jobs"
            isActive={activeTab === 'jobs'}
            onClick={() => setActiveTab('jobs')}
          />
          <AdminTabButton
            label="Logs"
            isActive={activeTab === 'logs'}
            onClick={() => setActiveTab('logs')}
          />
          <AdminTabButton
            label="KB Management"
            isActive={activeTab === 'kb-management'}
            onClick={() => setActiveTab('kb-management')}
          />
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'user-stats' && <AdminUserStatsSection />}
      {activeTab === 'cost' && <AdminCostSection />}
      {activeTab === 'jobs' && <AdminJobsSection />}
      {activeTab === 'logs' && <AdminLogsSection />}
      {activeTab === 'kb-management' && <AdminKBManagementSection />}
    </div>
  );
}

export function SettingsPage() {
  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

      <div className="space-y-8">
        {/* Admin Section with Tabs */}
        <SystemAdminOnly>
          <AdminSection />
        </SystemAdminOnly>

        {/* Integrations Section */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Integrations</h2>
          <LinkedInSetup />
        </section>
      </div>
    </div>
  );
}

export default SettingsPage;
